// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import Notify from "@guestsupply/App/Components/Notify/Notify";
import LazyImage, { LazyImageProps } from "@guestsupply/MobiusOverrides/LazyImage/LazyImage";
import TextField, { TextFieldPresentationProps } from "@guestsupply/MobiusOverrides/TextField/TextField";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBrandsDataView } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import loadBrands from "@insite/client-framework/Store/Data/Brands/Handlers/LoadBrands";
import translate from "@insite/client-framework/Translate";
import { BrandModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Clickable, GridContainer, GridItem, LoadingSpinner, Typography } from "@insite/mobius/index";
import { LinkProps } from "@insite/mobius/Link";
import { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useEffect, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    brandsSelected = "brandsSelected",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.brandsSelected]: string[];
    };
}

const mapStateToProps = (state: ApplicationState, props: OwnProps) => {
    const brandGalleryParameter = {
        brandIds: props.fields.brandsSelected,
        select: "id,name,detailPagePath,logoSmallImagePath,logoAltText",
    };
    const brandsState = getBrandsDataView(state, brandGalleryParameter);
    const shouldLoadBrandGallery = !brandsState.isLoading && !brandsState.value;

    return {
        brandsState,
        shouldLoadBrandGallery,
        brandGalleryParameter,
    };
};

const mapDispatchToProps = {
    loadBrandGallery: loadBrands,
};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandGridStyles {
    centeringWrapper?: InjectableCss;
    spinner?: LoadingSpinnerProps;
    brandGridWrapper?: InjectableCss;
    searchWrapper?: InjectableCss;
    search?: TextFieldPresentationProps;
    searchTitle?: TypographyProps;
    brandsContainer?: GridContainerProps;
    brandItem?: GridItemProps;
    link?: LinkProps;
    image?: LazyImageProps;
    brandTitle?: TypographyProps;
    brandItemWrapper?: InjectableCss;
    resultsLabel?: InjectableCss;
    noSearchResults?: InjectableCss;
}

export const brandGridStyles: BrandGridStyles = {};

const styles = brandGridStyles;

const BrandGrid = ({ brandsState, shouldLoadBrandGallery, brandGalleryParameter, loadBrandGallery }: Props) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [filteredBrandList, setFilteredBrandList] = useState<BrandModel[] | undefined>(brandsState.value);

    useEffect(() => {
        if (shouldLoadBrandGallery) {
            loadBrandGallery(brandGalleryParameter);
        }
    }, []);

    const { isLoading, value: brandList } = brandsState;

    useEffect(() => {
        setFilteredBrandList(brandList);
    }, [brandList]);

    if (isLoading || !brandList) {
        return (
            <StyledWrapper {...styles.centeringWrapper}>
                <LoadingSpinner {...styles.spinner} />
            </StyledWrapper>
        );
    }

    const searchQueryOnChangeHandler = (event: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(event.target.value);
        setFilteredBrandList(
            brandList.filter((brand: BrandModel) =>
                brand.name.toLowerCase().includes(event.target.value.toLowerCase()),
            ),
        );
    };

    return (
        <StyledWrapper {...styles.brandGridWrapper}>
            <StyledWrapper {...styles.searchWrapper}>
                <Typography {...styles.searchTitle}>{translate("Search by Name")}</Typography>
                <TextField
                    value={searchQuery}
                    onChange={searchQueryOnChangeHandler}
                    placeholder={translate("Type the brand name")}
                    {...styles.search}
                ></TextField>
            </StyledWrapper>
            {filteredBrandList?.length === 0 ? (
                <Notify type="warning" {...styles.noSearchResults}>
                    {translate("No Brands Found For: ")}
                    {searchQuery}
                </Notify>
            ) : (
                <StyledWrapper>
                    {searchQuery && (
                        <StyledWrapper {...styles.resultsLabel}>
                            {translate("Search Results For: ")}
                            {searchQuery}
                        </StyledWrapper>
                    )}
                    <GridContainer {...styles.brandsContainer}>
                        {filteredBrandList?.map((brand: BrandModel) => (
                            <GridItem key={brand.id} {...styles.brandItem}>
                                <Clickable
                                    href={brand.detailPagePath}
                                    {...styles.link}
                                    aria-label={translate(brand.name)}
                                >
                                    <StyledWrapper {...styles.brandItemWrapper}>
                                        <LazyImage
                                            width="185"
                                            height="185"
                                            {...styles.image}
                                            src={brand.logoSmallImagePath}
                                            altText={`${translate("Go to brand page")} ${
                                                brand.logoAltText || brand.name
                                            }`}
                                            customTransformations={{ default: "GUEST_Brand_185x185" }}
                                        />
                                        {/* eslint-disable-next-line spire/avoid-dynamic-translate */}
                                        <Typography {...styles.brandTitle}>{translate(brand.name)}</Typography>
                                    </StyledWrapper>
                                </Clickable>
                            </GridItem>
                        ))}
                    </GridContainer>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandGrid),
    definition: {
        group: "Brands",
        icon: "image",
        displayName: "Brand Grid",
        allowedContexts: ["BrandsPage", "HomePage"],
        fieldDefinitions: [
            {
                name: fields.brandsSelected,
                displayName: "Brands",
                editorTemplate: "SelectBrandsField",
                defaultValue: [],
                fieldType: "Contextual",
            },
        ],
    },
};

export default widgetModule;
